/* eslint-disable react/react-in-jsx-scope */
// import { postToApi } from 'venuescanner-js/services/api'
import loadExternalLibrary from 'venuescanner-js/utils/loadExternalLibrary';
import { waitFor } from 'venuescanner-js/utils/waitFor';
import {
  storedCoupon,
  storedPlan,
  availableTrialDays,
  storedTag,
} from 'venuescanner-js/services/localStorageServices';
import controllers from 'venuescanner-js/services/controllers';
import ENV from 'venuescanner-rails/app/javascript/environmentLoader';
import { popup } from 'venuescanner-js/vanilla/components/popup';

const chargebeeLoaded = () => {
  return window && window.Chargebee;
};
/** @class
 * @name Chargebee
 * @desc Chargebee class for processing Venue / Plan purchase
 * @param {object} options - The parameters for the Class
 * @param {Plan} options.plan - An instance of the Plan class
 * @param {Venue} options.venue - An instance of the Venue class
 * @param {Network} options.network - An instance of the Network class
 * @param {function} options.onShow - A callback triggered on the Chargebee widget displaying
 * @param {function} options.onError - A callback triggered on the Chargebee widget returning an Error
 * @param {function} options.onClose - A callback triggered on the Chargebee widget being closed
 * @param {function} options.onSuccess - A callback triggered on the Chargebee widget successfully applying the Plan / Subscription
 * @param {function} options.whichAccount - A callback that receives the selectAccount function where receiving true means use the Venue Account otherwise the Network account. Only called if there are both already created.
 */
const Chargebee = function ({
  plan,
  venue,
  network,
  coupon,
  onShow = () => {},
  onError = () => {},
  onClose = () => {},
  onSuccess = () => {},
  whichAccount = selectAccount => {
    selectAccount();
  },
}) {
  this.plan = plan;
  this.venue = venue;
  this.coupon = coupon;
  this.network = network;
  this.api = null;
  this.customer = null;
  /** @function
   * @name start
   * @desc Initiates the process. Called automatically
   * @memberof Chargebee
   */
  this.start = async function () {
    if (!this.venue) throw new Error('A Venue is required');
    if (!this.network) throw new Error('A Network is required');
    if (!this.plan) throw new Error('A Plan is required');
    // console.log({ plan, venue, network, coupon })
    try {
      if (this.network && this.venue) {
        // select which!
        const hasNetworkCustomer =
          this.network.chargebee && this.network.chargebee.customer;
        const hasVenueCustomer =
          this.venue.chargebee && this.venue.chargebee.customer;
        // console.log({ hasNetworkCustomer, hasVenueCustomer })
        const subscription = await venue.subscription();
        const customerId = subscription && subscription.chargebee_customer;

        if (!this.coupon) this.coupon = storedCoupon.get();
        // if already using a venue customer
        if (!hasNetworkCustomer && hasVenueCustomer)
          return this.selectAccount();
        if (
          customerId &&
          hasVenueCustomer &&
          venue.chargebee.customer === customerId
        )
          return this.selectAccount();

        if (!hasNetworkCustomer && !hasVenueCustomer)
          return this.selectAccount(false);
        return whichAccount((useVenueAccount = true) => {
          this.selectAccount(useVenueAccount);
        });
      } else {
        // attempt to auto-create a customer
        this.selectAccount();
      }
    } catch (e) {
      console.error(e);
    }
  };
  /** @function
   * @name selectAccount
   * @desc The method that defines which already created account to use.
   * @memberof Chargebee
   * @param {boolean} useVenue - If true, then use the Venue account, otherwise, use the Network account
   */
  this.selectAccount = async function (useVenue = true) {
    // popup.show('Opening customer setup...', { loading: true })
    const { data } = await controllers.chargebee.customer.post({
      plan: this.plan,
      venue: this.venue,
      network: useVenue ? null : this.network,
    });
    // const data = await postToApi('chargebee/customer', { plan: this.plan, venue: this.venue, network: useVenue ? null : this.network })
    // console.log(response)
    if (data.customer && data.customer.required) {
      // Request the missing fields
      // popup.show('There are some fields that need completing before we can move on...', { closable: true })
    } else {
      this.customer = data.customer;
      // console.log(this.customer)
    }
    if (useVenue && this.network) this.network = null;
    // we have a customer defined
    // popup.show('Opening customer details...', { loading: true, closable: true })
    this.loadChargebee(() => {
      this.processChargebee();
    });
  };
  /** @function
   * @name loadChargebee
   * @desc The method that loads and instantiates the Chargebee checkout libraries.
   * @memberof Chargebee
   * @param {function} loaded - Callback called on Chargebee being loaded
   */
  this.loadChargebee = function (loaded) {
    if (this.api) {
      loaded();
    } else if (chargebeeLoaded()) {
      window.Chargebee.init({
        site: ENV.chargebeeSite,
      });
      this.api = window.Chargebee.getInstance();
      loaded();
    } else {
      waitFor(
        chargebeeLoaded,
        () => {
          window.Chargebee.init({
            site: ENV.chargebeeSite,
          });
          this.api = window.Chargebee.getInstance();
          loaded();
        },
        {
          timedout: () => {
            popup.show('Failed to load the accounting system', {
              closable: true,
              success: false,
            });
          },
        },
      );
      loadExternalLibrary({ src: 'https://js.chargebee.com/v2/chargebee.js' });
    }
  };
  /** @function
   * @name fetchHostedPage
   * @desc Calls the Backend and creates a Chargebee hosted page, used for predefining the form to be loaded in the widget
   * @memberof Chargebee
   * @returns {object} - The hosted page info, including the URL to be loaded by the Checkout
   */
  this.fetchHostedPage = async function () {
    const forHostedPage = {
      plan_id: this.plan.chargebee,
    };
    if (this.customer && this.customer.id)
      forHostedPage.customer_id = this.customer && this.customer.id;
    if (this.network && this.network._id) {
      forHostedPage.network_id = this.network._id;
    }
    if (this.venue && this.venue._id) {
      forHostedPage.venue_id = this.venue._id;
      forHostedPage.venue_name = this.venue.name;
    } else {
      throw new Error('Missing either a network or a venue');
    }
    if (this.coupon) forHostedPage.coupon = this.coupon.id || this.coupon;
    const { data } = await controllers.chargebee.hostedPage.post(forHostedPage);
    // const data = await postToApi('chargebee/page_url', forHostedPage)
    // console.log({ data })
    return data.hosted_page;
  };
  /** @function
   * @name processChargebee
   * @memberof Chargebee
   * @desc Applied the fetched hosted page to the checkout Library and starts it
   */
  this.processChargebee = async function () {
    this.api.openCheckout({
      hostedPage: async () => {
        return this.fetchHostedPage();
      },
      loaded: onShow,
      error: onError,
      close: onClose,
      success: hostedPageId => {
        // success callback
        storedCoupon.set(null);
        storedPlan.set(null);
        availableTrialDays.set(null);
        storedTag.set(null);
        this.api.closeAll();
        window.dataLayer.push({
          event: `completed_${this.plan.tag}`,
          event_category: 'subscription',
          event_label: this.plan.chargebee,
        });
        onSuccess();
      },
    });
  };
  setTimeout(() => {
    this.start();
  }, 0);
};
export default Chargebee;
